import React, { useState, useEffect } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { AddProductToChargeDetails } from 'lane-shared/domains/billingPayments/types';
import {
  type SupportedPaymentCurrency,
  formatCurrency,
} from 'domains-billing-currency';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';

import useUserLocale from 'hooks/useUserLocale';

import styles from './addItemsToChargeStyles.scss';
import { getChargeAmounts } from '../helpers/getChargeAmounts';

export function AddedProductsTotal({
  listOfProducts,
  currency,
}: {
  listOfProducts: AddProductToChargeDetails[];
  currency: SupportedPaymentCurrency;
}) {
  const { t } = useTranslation();
  const isAdminFeeEnabled = useBpAdminFeeChargeCodeEnabled();
  const [summary, setSummary] = useState({
    subTotal: '',
    adminFeeTax: '',
    tax: '',
    total: '',
  });

  const twoDecimalsWithCurrency = formatCurrency(currency, {
    minimumFractionDigits: 2,
  });

  const locale = useUserLocale();

  useEffect(() => {
    const { subTotal, adminFeeTax, tax, total } = getChargeAmounts(
      listOfProducts,
      currency,
      locale,
      isAdminFeeEnabled
    );

    setSummary({
      subTotal: twoDecimalsWithCurrency(subTotal),
      adminFeeTax: twoDecimalsWithCurrency(adminFeeTax),
      tax: twoDecimalsWithCurrency(tax),
      total: twoDecimalsWithCurrency(total),
    });
  }, [listOfProducts, isAdminFeeEnabled]);

  return (
    <div className={styles.addedProductsSummary}>
      <div className={styles.summaryRow}>
        <div className={styles.label}>
          {t('abp.charges.addProduct.summary.subtotal')}
        </div>
        <div className={styles.value} id="create-charge-summary-subtotal">
          {summary.subTotal}
        </div>
      </div>
      {isAdminFeeEnabled && (
        <div className={styles.summaryRow}>
          <div className={styles.label}>
            {t('abp.charges.addProduct.summary.adminFeeTax')}
          </div>
          <div
            className={styles.value}
            id="create-charge-summary-admin-fee-tax"
          >
            {summary.adminFeeTax}
          </div>
        </div>
      )}
      <div className={styles.summaryRow}>
        <div className={styles.label}>
          {t('abp.charges.addProduct.summary.tax')}
        </div>
        <div className={styles.value} id="create-charge-summary-tax">
          {summary.tax}
        </div>
      </div>
      <div className={cx(styles.summaryRow, styles.total)}>
        <div className={styles.label}>
          {t('abp.charges.addProduct.summary.total')}
        </div>
        <div className={styles.value} id="create-charge-summary-total">
          {summary.total}
        </div>
      </div>
    </div>
  );
}
